import { useEffect, useMemo, useSyncExternalStore } from "react";
import { AutosaveController, type AutosaveOptions, type AutosaveSnapshot } from "./autosave";

export interface UseAutosaveResult extends AutosaveSnapshot {
  setValue: (key: string, value: unknown) => void;
  retry: () => void;
  discard: () => void;
  flush: () => void;
  acceptAuthoritativeValues: (values: AutosaveOptions["values"]) => void;
}

/** Pass a key containing user, company, pipeline/screen, and canonical record ID. */
export function useAutosave(options: AutosaveOptions): UseAutosaveResult {
  const controller = useMemo(() => new AutosaveController(options, false), [options.storageKey]);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  const valuesSignature = JSON.stringify(options.values);

  useEffect(() => {
    controller.activate();
    return () => controller.destroy();
  }, [controller]);
  useEffect(() => { controller.updateValues(options.values); }, [controller, valuesSignature]);
  useEffect(() => { controller.setEnabled(options.enabled); }, [controller, options.enabled]);
  useEffect(() => { controller.setSave(options.save); }, [controller, options.save]);

  return {
    ...snapshot,
    setValue: controller.setValue,
    retry: controller.retry,
    discard: controller.discard,
    flush: controller.flush,
    acceptAuthoritativeValues: (values) => controller.acceptAuthoritativeValues(values),
  };
}
